import { useRef, useState } from "react";
import { enviarArquivos, type ProgressoUpload } from "../../lib/api.js";
import { formatarTamanho, reduzirImagem } from "../../lib/imagem.js";
import { Botao, Falha, cx } from "../../components/ui.js";

export interface FotoEnviada {
  path: string;
  url: string;
}

/**
 * Captura da foto do espectador.
 *
 * Dois caminhos, e a ordem importa: "Tirar foto" vem primeiro porque é o que
 * acontece em 90% dos atendimentos — a pessoa está ali, na frente do operador.
 * O `capture="environment"` abre a câmera traseira direto, sem passar pelo
 * seletor de arquivos do sistema.
 */
export function EscolherFoto({
  fotoUrl,
  aoEnviar,
  aoLimpar,
}: {
  fotoUrl?: string | null;
  aoEnviar: (foto: FotoEnviada) => void;
  aoLimpar?: () => void;
}) {
  const camera = useRef<HTMLInputElement>(null);
  const galeria = useRef<HTMLInputElement>(null);

  const [progresso, setProgresso] = useState<ProgressoUpload | null>(null);
  const [preparando, setPreparando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [reducao, setReducao] = useState<string | null>(null);

  async function receber(arquivo: File | undefined) {
    if (!arquivo) return;

    setErro(null);
    setReducao(null);
    setPreparando(true);

    try {
      const original = arquivo.size;
      const reduzido = await reduzirImagem(arquivo);

      if (reduzido.size < original) {
        setReducao(`${formatarTamanho(original)} → ${formatarTamanho(reduzido.size)}`);
      }

      setPreparando(false);
      setProgresso({ enviados: 0, total: reduzido.size, percent: 0 });

      const r = await enviarArquivos<FotoEnviada>(
        "/api/generations/upload-photo",
        { nome: "photo", arquivos: [reduzido] },
        setProgresso,
      );

      aoEnviar(r);
    } catch (e) {
      setErro(e instanceof Error ? e.message : "Não foi possível enviar a foto");
    } finally {
      setPreparando(false);
      setProgresso(null);
    }
  }

  const ocupado = preparando || progresso !== null;

  if (fotoUrl && !ocupado) {
    return (
      <div className="flex flex-col gap-4">
        <div className="overflow-hidden rounded-peca border border-borda bg-black">
          <img src={fotoUrl} alt="Foto do espectador" className="w-full object-contain" />
        </div>
        <div className="flex gap-3">
          <Botao onClick={() => camera.current?.click()}>Tirar outra</Botao>
          {aoLimpar && (
            <Botao tom="fantasma" onClick={aoLimpar}>
              Remover
            </Botao>
          )}
        </div>
        <Entradas camera={camera} galeria={galeria} aoEscolher={receber} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <button
        onClick={() => camera.current?.click()}
        disabled={ocupado}
        className={cx(
          "flex aspect-[4/5] w-full flex-col items-center justify-center gap-3",
          "rounded-folha border-2 border-dashed border-borda-forte bg-superficie/40",
          "transition-colors hover:border-brasa hover:bg-superficie",
          "disabled:cursor-wait disabled:opacity-60",
        )}
      >
        {ocupado ? (
          <Ocupado preparando={preparando} progresso={progresso} />
        ) : (
          <>
            <IconeCamera />
            <span className="text-[1.0625rem] font-medium">Tirar foto</span>
            <span className="text-[0.875rem] text-texto-suave">Abre a câmera direto</span>
          </>
        )}
      </button>

      {!ocupado && (
        <Botao tom="fantasma" largo onClick={() => galeria.current?.click()}>
          Escolher da galeria
        </Botao>
      )}

      {reducao && (
        <p className="text-center font-mono text-[0.75rem] text-texto-fraco">
          Reduzida para envio: {reducao}
        </p>
      )}

      {erro && <Falha mensagem={erro} />}

      <Entradas camera={camera} galeria={galeria} aoEscolher={receber} />
    </div>
  );
}

function Ocupado({
  preparando,
  progresso,
}: {
  preparando: boolean;
  progresso: ProgressoUpload | null;
}) {
  return (
    <div className="flex w-full max-w-[16rem] flex-col items-center gap-3">
      <p className="text-[0.9375rem] text-texto-suave">
        {preparando ? "Preparando a imagem…" : "Enviando…"}
      </p>
      <div className="h-1 w-full overflow-hidden rounded-full bg-borda">
        <div
          className="h-full rounded-full bg-brasa transition-[width] duration-200"
          style={{ width: `${preparando ? 8 : (progresso?.percent ?? 0)}%` }}
        />
      </div>
      {progresso && (
        <p className="num font-mono text-[0.75rem] text-texto-fraco">{progresso.percent}%</p>
      )}
    </div>
  );
}

function Entradas({
  camera,
  galeria,
  aoEscolher,
}: {
  camera: React.RefObject<HTMLInputElement | null>;
  galeria: React.RefObject<HTMLInputElement | null>;
  aoEscolher: (a: File | undefined) => void;
}) {
  return (
    <>
      <input
        ref={camera}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        onChange={(e) => {
          aoEscolher(e.target.files?.[0]);
          e.target.value = "";
        }}
      />
      <input
        ref={galeria}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        hidden
        onChange={(e) => {
          aoEscolher(e.target.files?.[0]);
          e.target.value = "";
        }}
      />
    </>
  );
}

function IconeCamera() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="size-9 text-brasa"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path
        d="M3 8.5A2.5 2.5 0 0 1 5.5 6h1.7a1 1 0 0 0 .83-.45l.94-1.4A1 1 0 0 1 9.8 3.7h4.4a1 1 0 0 1 .83.45l.94 1.4a1 1 0 0 0 .83.45h1.7A2.5 2.5 0 0 1 21 8.5v8A2.5 2.5 0 0 1 18.5 19h-13A2.5 2.5 0 0 1 3 16.5z"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="12.2" r="3.4" />
    </svg>
  );
}
